import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { setRumViewName, startRumView } from '@restworld/utils-common';

/**
 * One RUM view per screen of /getting-started, named `getting-started/<step>/<sub_step>`.
 *
 * The RUM SDK opens a view only when the path changes, and the whole flow lives on one path
 * with `?step=&sub_step=`, so without this the venue search, the AI loading and the venue form
 * are a single view. Errors and API calls can be told apart anyway, since every RUM event
 * carries the URL of the moment; what a view per screen adds is per-screen INP, time spent and
 * action count. View-level numbers for /getting-started shift from the deploy of this hook.
 *
 * Calling this before RUM has started is safe: the name is held and applied on start.
 *
 * Reads the URL rather than the atoms because every way of moving (continue, back button,
 * refresh, redirects between sub-steps) ends up there. The first screen renames the page-load
 * view instead of starting a new one, so the load vitals (LCP, FCP) stay on it.
 */
export const useRumSubStepViews = () => {
  const router = useRouter();
  const step = router.query.step as string | undefined;
  const subStep = router.query.sub_step as string | undefined;
  const lastViewNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!step) return;
    const name = `getting-started/${step}/${subStep ?? 'none'}`;
    if (name === lastViewNameRef.current) return;

    if (lastViewNameRef.current === null) {
      setRumViewName(name);
    } else {
      startRumView(name);
    }
    lastViewNameRef.current = name;
  }, [step, subStep]);
};
